// Página AdminOffers - Gestión de ofertas (listado)
// Muestra tabla de ofertas con acciones CRUD

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useOffers } from '../../hooks/useOffers'
import { useOfferOperations } from '../../hooks/useOffers'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import ConfirmDialog from '../../components/ConfirmDialog'
import { formatDate } from '../../utils/helpers'
import './AdminOffers.css'

/**
 * Página AdminOffers
 * Listado y gestión de ofertas
 */
function AdminOffers() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all') // all, active, inactive, confirmed, pending
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deactivateConfirm, setDeactivateConfirm] = useState(null)
  const [notification, setNotification] = useState(null)

  // Construir filtros
  const filters = {}
  if (filterStatus === 'active') filters.active = true
  else if (filterStatus === 'inactive') filters.active = false
  else if (filterStatus === 'confirmed') filters.confirmed = true
  else if (filterStatus === 'pending') filters.confirmed = false

  // Obtener ofertas
  const { offers, loading, error, refetch } = useOffers(filters)
  const { delete: deleteOffer, deactivate, loading: operationLoading } = useOfferOperations()

  // Filtrar ofertas por búsqueda
  const filteredOffers = offers.filter(offer => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      (offer.name || '').toLowerCase().includes(query) ||
      (offer.offerNumber || '').toLowerCase().includes(query)
    )
  })

  // Manejar eliminación de oferta
  const handleDelete = async (offerId) => {
    try {
      await deleteOffer(offerId)
      setDeleteConfirm(null)
      setNotification({
        type: 'success',
        message: 'Oferta eliminada correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
      refetch()
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Error al eliminar la oferta'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Manejar desactivación de oferta
  const handleDeactivate = async (offerId) => {
    try {
      await deactivate(offerId)
      setDeactivateConfirm(null)
      setNotification({
        type: 'success',
        message: 'Oferta desactivada correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
      refetch()
    } catch (err) {
      setNotification({
        type: 'error',
        message: err.message || 'Error al desactivar la oferta'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  // Verificar si una oferta está activa (dentro de fechas)
  const isOfferActive = (offer) => {
    if (!offer.active || !offer.confirmed) return false
    const now = new Date()
    const startDate = offer.startDate?.toDate ? offer.startDate.toDate() : new Date(offer.startDate)
    const endDate = offer.endDate?.toDate ? offer.endDate.toDate() : new Date(offer.endDate)
    return now >= startDate && now <= endDate
  }

  if (loading) {
    return (
      <div className="admin-offers-page">
        <div className="loading-state">
          <p>Cargando ofertas...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="admin-offers-page">
      <div className="page-header">
        <div>
          <h1>Gestión de Ofertas</h1>
          <p>Administra los lotes de ofertas y promociones</p>
        </div>
        <Link to="/admin/ofertas/nuevo">
          <Button variant="primary">
            ➕ Nueva Oferta
          </Button>
        </Link>
      </div>

      {/* Filtros y búsqueda */}
      <div className="filters-section">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Buscar por nombre o número de oferta..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">Todas las ofertas</option>
            <option value="active">Activas</option>
            <option value="inactive">Inactivas</option>
            <option value="confirmed">Confirmadas</option>
            <option value="pending">Pendientes</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="error-state">
          <p>Error: {error}</p>
          <Button onClick={refetch}>Reintentar</Button>
        </div>
      )}

      {/* Tabla de ofertas */}
      {!loading && !error && (
        <>
          <div className="offers-count">
            Mostrando {filteredOffers.length} oferta(s)
          </div>

          {filteredOffers.length > 0 ? (
            <div className="offers-table-container">
              <table className="offers-table">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Nombre</th>
                    <th>Fecha Inicio</th>
                    <th>Fecha Fin</th>
                    <th>Productos</th>
                    <th>Estado</th>
                    <th>Confirmada</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOffers.map((offer) => {
                    const active = isOfferActive(offer)
                    const productsCount = offer.products?.filter(p => !p.excluded).length || 0
                    
                    return (
                      <tr key={offer.id} className={!offer.active ? 'inactive-row' : ''}>
                        <td>
                          <strong>{offer.offerNumber || '-'}</strong>
                        </td>
                        <td>
                          <Link to={`/admin/ofertas/${offer.id}`} className="offer-link">
                            {offer.name || 'Sin nombre'}
                          </Link>
                        </td>
                        <td>{formatDate(offer.startDate)}</td>
                        <td>{formatDate(offer.endDate)}</td>
                        <td>{productsCount} producto(s)</td>
                        <td>
                          <span className={`status-badge ${active ? 'active' : 'inactive'}`}>
                            {active ? '🟢 Activa' : offer.confirmed ? '✅ Confirmada' : '⏸️ Inactiva'}
                          </span>
                        </td>
                        <td>
                          {offer.confirmed ? (
                            <span className="confirmed-badge">✓ Confirmada</span>
                          ) : (
                            <span className="pending-badge">⏳ Pendiente</span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            <Link to={`/admin/ofertas/${offer.id}`}>
                              <Button variant="outline" size="small">
                                👁️ Ver
                              </Button>
                            </Link>
                            {!offer.confirmed && (
                              <Link to={`/admin/ofertas/${offer.id}/editar`}>
                                <Button variant="outline" size="small">
                                  ✏️ Editar
                                </Button>
                              </Link>
                            )}
                            {offer.confirmed && offer.active && (
                              <Button
                                variant="danger"
                                size="small"
                                onClick={() => setDeactivateConfirm(offer.id)}
                                disabled={operationLoading}
                              >
                                ⏸️ Desactivar
                              </Button>
                            )}
                            {!offer.confirmed && (
                              <Button
                                variant="danger"
                                size="small"
                                onClick={() => setDeleteConfirm(offer.id)}
                                disabled={operationLoading}
                              >
                                🗑️ Eliminar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No hay ofertas disponibles</p>
              <Link to="/admin/ofertas/nuevo">
                <Button variant="primary">Crear primera oferta</Button>
              </Link>
            </div>
          )}
        </>
      )}

      {/* Diálogos de confirmación */}
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDelete(deleteConfirm)}
        title="Eliminar Oferta"
        message="¿Estás seguro de eliminar esta oferta? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="danger"
      />

      <ConfirmDialog
        isOpen={!!deactivateConfirm}
        onClose={() => setDeactivateConfirm(null)}
        onConfirm={() => handleDeactivate(deactivateConfirm)}
        title="Desactivar Oferta"
        message="¿Estás seguro de desactivar esta oferta? Se limpiarán los precios especiales de los productos afectados."
        confirmText="Desactivar"
        cancelText="Cancelar"
        variant="warning"
      />

      {/* Notificaciones */}
      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  )
}

export default AdminOffers

